import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Package, FolderTree, ShoppingCart, Users, Ticket, LayoutTemplate, Flame, ScrollText, Settings, LogOut, Store, Menu, X, BarChart3, Gavel } from "lucide-react";
import { useStore } from "../context/StoreContext";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/produtos", icon: Package, label: "Produtos" },
  { to: "/admin/categorias", icon: FolderTree, label: "Categorias" },
  { to: "/admin/pedidos", icon: ShoppingCart, label: "Pedidos" },
  { to: "/admin/estoque", icon: BarChart3, label: "Estoque & Vendas" },
  { to: "/admin/leiloes", icon: Gavel, label: "Leilões" },
  { to: "/admin/clientes", icon: Users, label: "Clientes" },
  { to: "/admin/cupons", icon: Ticket, label: "Cupons" },
  { to: "/admin/conteudo", icon: LayoutTemplate, label: "Banners & Blocos" },
  { to: "/admin/dragoes", icon: Flame, label: "Dragões 3D" },
  { to: "/admin/logs", icon: ScrollText, label: "Logs" },
  { to: "/admin/configuracoes", icon: Settings, label: "Configurações" },
];

export default function AdminLayout() {
  const { user, authChecked, logout } = useStore();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (authChecked && (!user || user.role !== "admin")) navigate("/login");
  }, [authChecked, user, navigate]);

  if (!user || user.role !== "admin") return <div className="min-h-screen bg-background" data-testid="admin-guard" />;

  const sidebarContent = (
    <>
      <div className="p-6 border-b border-white/10 flex items-center justify-between">
        <div>
          <Link to="/admin" className="flex items-center gap-2.5" data-testid="admin-logo" onClick={() => setMenuOpen(false)}>
            <img src="/assets/dragon-head.webp" alt="" className="h-9 w-auto" />
            <img src="/assets/wordmark.webp" alt="ZEFERIUS" className="h-5 w-auto" />
          </Link>
          <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 mt-1">Painel Admin</p>
        </div>
        <button className="lg:hidden text-zinc-400 hover:text-gold p-1" onClick={() => setMenuOpen(false)} data-testid="admin-menu-close" aria-label="Fechar menu">
          <X size={20} />
        </button>
      </div>
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="admin-nav">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setMenuOpen(false)} data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive ? "bg-amber-500/10 text-gold border border-gold/20" : "text-zinc-400 hover:text-gold hover:bg-white/5 border border-transparent"
              }`
            }>
            <item.icon size={16} /> {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10 space-y-1">
        <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-400 hover:text-gold hover:bg-white/5 transition-colors" data-testid="admin-view-store">
          <Store size={16} /> Ver loja
        </Link>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-400 hover:text-red-400 hover:bg-white/5 transition-colors">
          <LogOut size={16} /> Sair
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background lg:flex" data-testid="admin-layout">
      {/* Topbar mobile */}
      <div className="lg:hidden sticky top-0 z-40 bg-[#0c0c0c]/95 backdrop-blur border-b border-white/10 px-4 h-14 flex items-center justify-between" data-testid="admin-mobile-topbar">
        <button onClick={() => setMenuOpen(true)} className="text-zinc-200 hover:text-gold p-1" data-testid="admin-menu-toggle" aria-label="Abrir menu">
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <img src="/assets/dragon-head.webp" alt="" className="h-7 w-auto" />
          <img src="/assets/wordmark.webp" alt="ZEFERIUS" className="h-4 w-auto" />
        </div>
        <span className="w-8" />
      </div>

      {/* Overlay mobile */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/70 z-40 lg:hidden" onClick={() => setMenuOpen(false)} data-testid="admin-menu-backdrop" />
      )}

      {/* Sidebar: lateral fixa no desktop, drawer deslizante no mobile */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 shrink-0 border-r border-white/10 bg-[#0c0c0c] flex flex-col transform transition-transform duration-300 ${menuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="admin-sidebar">
        {sidebarContent}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <main className="flex-1 p-4 sm:p-6 lg:p-10 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
